import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SecurityPanel = ({ securityData, onViewAuditLog, onViewSessions }) => {
  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical':
        return { icon: 'AlertTriangle', color: 'text-error' };
      case 'warning':
        return { icon: 'AlertCircle', color: 'text-warning' };
      case 'info':
        return { icon: 'Info', color: 'text-accent' };
      default:
        return { icon: 'Bell', color: 'text-muted-foreground' };
    }
  };

  return (
    <div className="space-y-6">
      {/* Security Overview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Security Overview</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={onViewAuditLog}
            iconName="FileText"
            iconPosition="left"
          >
            View Audit Log
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Failed Login Attempts (24h)</span>
              <span className="text-sm font-medium text-foreground">{securityData?.failedLogins}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Active Sessions</span>
              <span className="text-sm font-medium text-foreground">{securityData?.activeSessions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Password Resets (7d)</span>
              <span className="text-sm font-medium text-foreground">{securityData?.passwordResets}</span>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Suspicious Activities</span>
              <span className="text-sm font-medium text-error">{securityData?.suspiciousActivities}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Account Lockouts</span>
              <span className="text-sm font-medium text-warning">{securityData?.accountLockouts}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">2FA Enabled Users</span>
              <span className="text-sm font-medium text-success">{securityData?.twoFactorEnabled}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Security Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Recent Security Alerts</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={onViewSessions}
            iconName="Shield"
            iconPosition="left"
          >
            Manage Sessions
          </Button>
        </div>
        
        <div className="space-y-3">
          {securityData?.recentAlerts?.map((alert) => {
            const alertConfig = getAlertIcon(alert?.type);
            return (
              <div key={alert?.id} className="flex items-start space-x-3 p-3 border border-border rounded-lg">
                <Icon 
                  name={alertConfig?.icon} 
                  size={16} 
                  className={`mt-0.5 ${alertConfig?.color}`} 
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{alert?.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{alert?.description}</p>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className="text-xs text-muted-foreground">
                      {formatDate(alert?.timestamp)}
                    </span>
                    {alert?.user && (
                      <span className="text-xs text-muted-foreground">
                        User: {alert?.user}
                      </span>
                    )}
                    {alert?.ip && (
                      <span className="text-xs text-muted-foreground">
                        IP: {alert?.ip}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ExternalLink"
                  iconSize={14}
                >
                  Details
                </Button>
              </div>
            );
          })}
        </div>
      </div>
      {/* Active Sessions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Active User Sessions</h3>
        
        <div className="space-y-3">
          {securityData?.activeSessions > 0 ? (
            securityData?.recentSessions?.map((session) => (
              <div key={session?.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={16} color="white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{session?.user}</p>
                    <p className="text-xs text-muted-foreground">
                      {session?.location} • {session?.device}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Last Active</p>
                    <p className="text-xs text-foreground">{formatDate(session?.lastActive)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="LogOut"
                    iconSize={14}
                  >
                    End Session
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Icon name="Shield" size={48} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No active sessions to display</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityPanel;